// Flat React Query hook collection wrapping the API client.
// Extend with your solution-specific hooks as features are added.
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';

// User
export function useUser(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['user'],
    queryFn: () => api.getUser(),
    retry: 1,
    staleTime: 30_000,
    // Defaults to enabled (preserving callers that always have a session).
    // Public pages like /pricing pass `enabled: isAuthenticated` so we don't
    // fire api.getUser() for unauth visitors — the global apiRequest helper
    // redirects to /signin on auth failure, which would otherwise bounce
    // crawlers and prospective customers off the page on mount.
    enabled: options.enabled ?? true,
  });
}

// Billing
export function useCreateCheckout() {
  return useMutation({
    mutationFn: (plan: string) => api.createCheckout(plan),
    onSuccess: ({ url }) => {
      window.location.href = url;
    }
  });
}

export function useCreatePortal() {
  return useMutation({
    mutationFn: () => api.createPortal(),
    onSuccess: ({ url }) => {
      window.location.href = url;
    }
  });
}

// API keys
export function useApiKeys() {
  return useQuery({
    queryKey: ['apiKeys'],
    queryFn: () => api.listApiKeys(),
    staleTime: 30_000
  });
}

export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.createApiKey(name),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['apiKeys'] }); }
  });
}

export function useDeleteApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => api.deleteApiKey(key),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['apiKeys'] }); }
  });
}

// Support tickets
export function useSupportTickets() {
  return useQuery({
    queryKey: ['support-tickets'],
    queryFn: () => api.listSupportTickets(),
    staleTime: 30_000
  });
}

export function useSupportTicket(ticketId: string) {
  return useQuery({
    queryKey: ['support-ticket', ticketId],
    queryFn: () => api.getSupportTicket(ticketId),
    enabled: !!ticketId,
    staleTime: 15_000
  });
}

export function useCreateSupportTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subject, message, category }: { subject: string; message: string; category: string }) =>
      api.createSupportTicket(subject, message, category),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['support-tickets'] }); }
  });
}

export function useReplySupportTicket(ticketId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (message: string) => api.replySupportTicket(ticketId, message),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['support-ticket', ticketId] });
      void qc.invalidateQueries({ queryKey: ['support-tickets'] });
    }
  });
}
